// authReducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AsyncStage } from '../reduxCommon';
import { User } from '../models/user';

interface AuthState {
	stage: AsyncStage;
	error: string;
	user: User;
	astroToken: string;
	googleAccessToken: string;
	googleIdToken: string;
	// Onboarding authentication state
	onboardingToken: string;
	onboardingPassword: string;
	onboardingSessionToken: string;
	onboardingSessionExpiresAt: string;
	onboardingExternalUser: any; // CompanyExternalUser type
	isOnboardingAuthenticated: boolean;
}

const defaultUser: User = {
	id: '',
	email: '',
	firstName: '',
	lastName: '',
	preferredName: '',
	preferredPronouns: '',
	personalEmail: '',
	role: '',
	status: '',
	permissions: '',
	created: '',
	updated: '',
	alwaysAddToProjects: false,
	acceptedInvite: false,
	acceptedTermsAndConditions: false,
	acceptedTermsAndConditionsDate: '',
	googleId: '',
	preferences: {
		theme: 'orbito',
	},
	helpers: {
		tourTooltipViewed: false,
	},
};

const initialState: AuthState = {
	stage: AsyncStage.NoOp,
	error: '',
	user: defaultUser,
	astroToken: '',
	googleAccessToken: '',
	googleIdToken: '',
	// Onboarding authentication state
	onboardingToken: '',
	onboardingPassword: '',
	onboardingSessionToken: '',
	onboardingSessionExpiresAt: '',
	onboardingExternalUser: null,
	isOnboardingAuthenticated: false,
};

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		asyncCallStart: (state: AuthState) => {
			state.stage = AsyncStage.Busy;
		},
		asyncCallDone: (state: AuthState) => {
			state.stage = AsyncStage.NoOp;
		},
		asyncCallError: (state: AuthState, action: PayloadAction<any>) => {
			state.stage = AsyncStage.Error;
			state.error = action.payload.message || 'UNKNOWN';
		},
		loginSuccess: (
			state: AuthState,
			action: PayloadAction<{
				user: User;
				astroToken: string;
			}>
		) => {
			state.user = action.payload.user;
			state.astroToken = action.payload.astroToken;
			state.stage = AsyncStage.Success;
		},
		setAstroToken: (state: AuthState, action: PayloadAction<string>) => {
			state.astroToken = action.payload || '';
		},
		logout: (state: AuthState) => {
			state.user = defaultUser;
			state.googleAccessToken = '';
			state.googleIdToken = '';
			state.astroToken = '';
			state.stage = AsyncStage.NoOp;
		},
		updateSelfSuccess: (
			state: AuthState,
			action: PayloadAction<{
				user: User;
			}>
		) => {
			state.user = { ...state.user, ...action.payload.user };
		},
		updateProfilePictureSuccess: (
			state: AuthState,
			action: PayloadAction<{
				googleProfilePictureUrl: string;
			}>
		) => {
			state.user = {
				...state.user,
				googleProfilePictureUrl: action.payload.googleProfilePictureUrl,
			};
		},
		// Onboarding authentication actions
		onboardingAuthSuccess: (
			state: AuthState,
			action: PayloadAction<{
				token: string;
				password: string;
				sessionToken?: string;
				expiresAt?: string;
				externalUser: any;
			}>
		) => {
			state.onboardingToken = action.payload.token;
			state.onboardingPassword = action.payload.password;
			state.onboardingSessionToken = action.payload.sessionToken || '';
			state.onboardingSessionExpiresAt = action.payload.expiresAt || '';
			state.onboardingExternalUser = action.payload.externalUser;
			state.isOnboardingAuthenticated = true;
			state.stage = AsyncStage.Success;
		},
		clearOnboardingAuth: (state: AuthState) => {
			state.onboardingToken = '';
			state.onboardingPassword = '';
			state.onboardingSessionToken = '';
			state.onboardingSessionExpiresAt = '';
			state.onboardingExternalUser = null;
			state.isOnboardingAuthenticated = false;
			state.stage = AsyncStage.NoOp;
		},
		onboardingAuthError: (
			state: AuthState,
			action: PayloadAction<string>
		) => {
			state.error = action.payload;
			state.stage = AsyncStage.Error;
		},
		updateOnboardingPassword: (
			state: AuthState,
			action: PayloadAction<string>
		) => {
			state.onboardingPassword = action.payload;
		},
	},
});

export const {
	asyncCallStart,
	asyncCallDone,
	asyncCallError,
	loginSuccess,
	setAstroToken,
	logout,
	updateSelfSuccess,
	updateProfilePictureSuccess,
	onboardingAuthSuccess,
	clearOnboardingAuth,
	onboardingAuthError,
	updateOnboardingPassword,
} = authSlice.actions;

export default authSlice.reducer;
